import React from 'react';
import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import {
  SystemEventsWidget,
  SystemEventsWidgetOptions,
} from 'types/dashboard_widget';
import { LUMEO_COMMON_EVENT_TYPES, LUMEO_EVENT_SEVERITIES } from 'types/event';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { useModal } from 'hooks/useModal';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useCameras } from 'cameras/hooks/useCameras';
import { useDeployments } from 'deployments/hooks/useDeployments';
import { useGateways } from 'gateways/hooks/useGateways';
import { useStreams } from 'streams/hooks/useStreams';
import { useInfiniteEvents } from 'events/hooks/useInfiniteEvents';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Select } from 'components/Select/Select';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import { DeploymentSelect } from 'deployments/components/DeploymentSelect/DeploymentSelect';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { MultiSelectInput } from 'components/MultiSelectInput/MultiSelectInput';
import { Button } from 'components/Button/Button';
import { Loader } from 'components/Loader/Loader';
import { formatPretty } from 'services/string';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

const ENTITY_FILTER_OPTIONS = [
  { label: 'Cameras', value: 'camera_ids' },
  { label: 'Workflow deployments', value: 'deployment_ids' },
  { label: 'Gateways', value: 'gateway_ids' },
  { label: 'Streams', value: 'stream_ids' },
] as const;

const SEVERITY_OPTIONS = LUMEO_EVENT_SEVERITIES.map((value) => ({
  value,
  label: formatPretty(value),
}));

type SystemEventsWidgetFieldValues = {
  name: string;
  limit: number;
  camera_ids: Array<string>;
  deployment_ids: Array<string>;
  gateway_ids: Array<string>;
  stream_ids: Array<string>;
  event_types: Array<string>;
  severities: Array<'error' | 'warning' | 'info' | 'debug'>;
};

type SystemEventsFormProps = {
  widget?: SystemEventsWidget;
  dashboardId: string;
};

export function SystemEventsForm({
  widget,
  dashboardId,
}: SystemEventsFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options = widget?.options || {};

  const {
    camera_ids = [],
    deployment_ids = [],
    gateway_ids = [],
    stream_ids = [],
    event_types = [],
    severities = [],
  } = options;

  // Fetch initial entities for default values
  const { data: initialCameras, isInitialLoading: isCamerasLoading } =
    useCameras({
      params: { camera_ids, limit: camera_ids.length },
      enabled: camera_ids.length > 0,
    });

  const { data: initialDeployments, isInitialLoading: isDeploymentsLoading } =
    useDeployments({
      params: { deployment_ids, limit: deployment_ids.length },
      enabled: deployment_ids.length > 0,
      keepPreviousData: true,
    });

  const { data: initialGateways, isInitialLoading: isGatewaysLoading } =
    useGateways({
      params: { gateway_ids, limit: gateway_ids.length },
      enabled: gateway_ids.length > 0,
    });

  const { data: initialStreams, isInitialLoading: isStreamsLoading } =
    useStreams({
      params: { stream_ids, limit: stream_ids.length },
      enabled: stream_ids.length > 0,
    });

  // Fetch events to get available event types
  const { data: eventsData } = useInfiniteEvents({
    params: { limit: 100 },
  });

  const allEvents = eventsData?.pages.flatMap(({ data }) => data) ?? [];
  const availableEventTypes = Array.from(
    new Set([
      ...LUMEO_COMMON_EVENT_TYPES,
      ...allEvents.map((event) => event.event_type),
    ])
  ).sort();

  const eventTypeOptions = availableEventTypes.map((type) => ({
    value: type,
    label: type,
  }));

  const isLoading =
    isCamerasLoading ||
    isDeploymentsLoading ||
    isGatewaysLoading ||
    isStreamsLoading;

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess: () => close(),
  });

  const {
    formState: { errors, dirtyFields },
    control,
    handleSubmit,
    setValue,
    register,
  } = useForm<SystemEventsWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      limit: widget?.limit ?? 10,
      camera_ids: camera_ids || [],
      deployment_ids: deployment_ids || [],
      gateway_ids: gateway_ids || [],
      stream_ids: stream_ids || [],
      event_types: event_types || [],
      severities: severities || [],
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [entityFilters, setEntityFilters] = React.useState<
    Array<{
      entityType:
        | 'camera_ids'
        | 'deployment_ids'
        | 'gateway_ids'
        | 'stream_ids'
        | 'undecided';
    }>
  >(() => {
    const initialFilters = [];

    if (camera_ids.length > 0) {
      initialFilters.push({ entityType: 'camera_ids' as const });
    }
    if (deployment_ids.length > 0) {
      initialFilters.push({ entityType: 'deployment_ids' as const });
    }
    if (gateway_ids.length > 0) {
      initialFilters.push({ entityType: 'gateway_ids' as const });
    }
    if (stream_ids.length > 0) {
      initialFilters.push({ entityType: 'stream_ids' as const });
    }

    return initialFilters;
  });

  function onSubmit({
    name,
    limit,
    camera_ids,
    deployment_ids,
    gateway_ids,
    stream_ids,
    event_types,
    severities,
  }: SystemEventsWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();

    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 6, 6);

    const options: SystemEventsWidgetOptions | undefined = (() => {
      const opts: SystemEventsWidgetOptions = {
        ...(camera_ids.length > 0 && { camera_ids }),
        ...(deployment_ids.length > 0 && { deployment_ids }),
        ...(gateway_ids.length > 0 && { gateway_ids }),
        ...(stream_ids.length > 0 && { stream_ids }),
        ...(event_types.length > 0 && { event_types }),
        ...(severities.length > 0 && { severities }),
      };
      return Object.keys(opts).length > 0 ? opts : undefined;
    })();

    const updatedDashboard = upsertWidget(
      dashboard,
      new SystemEventsWidget(id, name, limit, layout, options)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  function getAvailableEntityOptions() {
    return ENTITY_FILTER_OPTIONS.filter(
      (option) =>
        !new Set(
          entityFilters
            .filter((filter) => filter.entityType !== 'undecided')
            .map((filter) => filter.entityType)
        ).has(option.value)
    );
  }

  const canAddEntityFilter =
    entityFilters.length < ENTITY_FILTER_OPTIONS.length;

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="system-events" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="Events per page" error={errors.limit}>
        <Input
          {...register('limit', {
            required: 'Please specify the number of events per page.',
            valueAsNumber: true,
            min: { value: 1, message: 'Events per page must be at least 1.' },
            max: {
              value: 100,
              message: 'Events per page must be at most 100.',
            },
          })}
          type="number"
          min={1}
          max={100}
        />
      </Field>

      <Field
        label="Entity Filters"
        error={
          errors.camera_ids ||
          errors.deployment_ids ||
          errors.gateway_ids ||
          errors.stream_ids
        }
      >
        {isLoading ? (
          <Loader size="small" text="Loading filters" />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {entityFilters.map((filter, index) => (
                <div
                  className="grid grid-cols-[1fr_3fr_auto] items-center gap-2"
                  key={`${filter.entityType}-${index}`}
                >
                  <div>
                    <Select
                      aria-label="Select entity type"
                      options={getAvailableEntityOptions()}
                      value={ENTITY_FILTER_OPTIONS.find(
                        ({ value }) => value === filter.entityType
                      )}
                      onChange={(option) => {
                        if (!option) return;

                        setEntityFilters((previousFilters) =>
                          previousFilters.map((f, i) => {
                            if (i === index) {
                              if (f.entityType !== 'undecided') {
                                setValue(f.entityType, []);
                              }
                              // Initialize the new field
                              if (option.value !== ('undecided' as any)) {
                                setValue(option.value, [], {
                                  shouldDirty: true,
                                });
                              }
                              return { entityType: option.value };
                            }
                            return f;
                          })
                        );
                      }}
                    />
                  </div>
                  <div>
                    {filter.entityType !== 'undecided' ? (
                      <Controller
                        control={control}
                        name={filter.entityType}
                        render={({ field: { onChange } }) => {
                          switch (filter.entityType) {
                            case 'camera_ids':
                              return (
                                <CameraSelect
                                  defaultValue={initialCameras?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(options.map((camera) => camera.id))
                                  }
                                  isMulti
                                />
                              );
                            case 'deployment_ids':
                              return (
                                <DeploymentSelect
                                  defaultValue={initialDeployments?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((deployment) => deployment.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'gateway_ids':
                              return (
                                <GatewaySelect
                                  defaultValue={initialGateways?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((gateway) => gateway.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'stream_ids':
                              return (
                                <StreamSelect
                                  defaultValue={initialStreams?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(options.map((stream) => stream.id))
                                  }
                                  isMulti
                                />
                              );
                            default:
                              return <Select options={[]} isDisabled />;
                          }
                        }}
                        rules={{}}
                      />
                    ) : (
                      <Select options={[]} isDisabled />
                    )}
                  </div>
                  <IconButton
                    icon="cancel"
                    label="Remove filter"
                    onClick={() => {
                      setEntityFilters((previousFilters) => {
                        const filterToRemove = previousFilters[index];
                        if (filterToRemove.entityType !== 'undecided') {
                          setValue(filterToRemove.entityType, []);
                        }
                        return previousFilters.filter((_, i) => i !== index);
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setEntityFilters((previousFilters) => [
                    ...previousFilters,
                    { entityType: 'undecided' },
                  ]);
                  // Trigger form validation to ensure the form is aware of the change
                }}
                disabled={
                  !canAddEntityFilter ||
                  isLoading ||
                  entityFilters.at(-1)?.entityType === 'undecided'
                }
              >
                <Icon name="plus" size="xsmall" />
                <span>Add entity filter</span>
              </Button>
            </div>
          </>
        )}
      </Field>

      <Field label="Event Types" error={errors.event_types}>
        <Controller
          control={control}
          name="event_types"
          render={({ field: { onChange, value } }) => (
            <MultiSelectInput
              id="event-types-filter"
              defaultSelected={value || []}
              onChange={(selected) => onChange(selected ?? [])}
              options={eventTypeOptions.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
              allowCreate={true}
            />
          )}
        />
      </Field>

      <Field label="Severities" error={errors.severities}>
        <Controller
          control={control}
          name="severities"
          render={({ field: { onChange, value } }) => (
            <Select
              options={SEVERITY_OPTIONS}
              value={SEVERITY_OPTIONS.filter((opt) =>
                value?.includes(opt.value)
              )}
              onChange={(options) =>
                onChange(options ? options.map((opt) => opt.value) : [])
              }
              isMulti
              placeholder="Select severities"
            />
          )}
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating || isLoading} />
    </form>
  );
}
